import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
    Form,
    Input,
    message,
    Tag,
    Calendar,
    Space,
    Tooltip,
    Popover,
    Modal,
    Table,
    Select,
    Upload,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import MailComposer from '../email/overview/MailComposer';
import { Main } from '../styled';
import { Cards } from '../../components/cards/frame/cards-frame';
import { Button } from '../../components/buttons/buttons';
import { useDispatch, useSelector } from 'react-redux';
import { personDataRead } from '../../redux/person/actionCreator';
import { alertModal } from '../../components/modals/antd-modals';
import {
    getEmployeeLeavesData,
    getLeavesDataRequestData,
    mailDataSubmit,
} from '../../redux/mail/actionCreator';
import CustomSpinner from '../ui-elements/spinner/CustomSpinner';

function LeaveEmail() {
    const dispatch = useDispatch();

    const [initialSubject, setInitialSubject] = useState('');
    const [leaveType, setLeaveType] = useState(null);
    const [form] = Form.useForm();

    const [dates, setDates] = useState([]);
    const [panelValue, setPanelValue] = useState(dayjs());
    const [calendarOpen, setCalendarOpen] = useState(false);

    const [leavesModalOpen, setLeavesModalOpen] = useState(false);
    const [leavesModalTab, setLeavesModalTab] = useState('Calendar');
    const [modalCalValue, setModalCalValue] = useState(dayjs());

    const [attachmentFile, setAttachmentFile] = useState(null);
    const [attachmentList, setAttachmentList] = useState([]);

    const containerRef = useRef(null);

    const {
        isMailAddResLoading,
        isPersonResLoading,
        MailAddRes,
        employeeLeavesRes,
        leaveData,
    } = useSelector((state) => ({
        isPersonResLoading: state.PersonRes.loading,
        isMailAddResLoading: state.MailAddRes.loading,
        MailAddRes: state.MailAddRes.data,
        employeeLeavesRes: state.GetEmployeeLeavesData.data,
        leaveData: state.GetLeaveData.data,
    }));

    useEffect(() => {
        dispatch(personDataRead());
        dispatch(getEmployeeLeavesData());
        dispatch(getLeavesDataRequestData());
    }, [dispatch]);

    useEffect(() => {
        if (MailAddRes !== null && MailAddRes?.Error === false) {
            form.resetFields();
            setInitialSubject('');
            setLeaveType(null);
            setDates([]);
            setPanelValue(dayjs());
            setCalendarOpen(false);
            setAttachmentFile(null);
            setAttachmentList([]);
        }
    }, [MailAddRes, form]);

    const toISO = (d) => dayjs(d).format('YYYY-MM-DD');

    const isSunday = (d) => dayjs(d).day() === 0;

    const isSecondSaturday = (d) => {
        const dd = dayjs(d);
        if (dd.day() !== 6) return false; // Saturday only
        const weekOfMonth = Math.ceil(dd.date() / 7);
        return weekOfMonth === 2;
    };

    const isExcludedDay = (d) => isSunday(d) || isSecondSaturday(d);

    const nextWorkingDay = (d) => {
        let cur = dayjs(d).add(1, 'day');
        while (isExcludedDay(cur)) cur = cur.add(1, 'day');
        return cur;
    };

    const isContinuousWorkingDates = (arr) => {
        if (!Array.isArray(arr) || arr.length <= 1) return true;
        const sorted = [...arr].sort();
        for (let i = 1; i < sorted.length; i += 1) {
            const prev = dayjs(sorted[i - 1]);
            const curr = dayjs(sorted[i]);
            const expected = nextWorkingDay(prev);
            if (!curr.isSame(expected, 'day')) return false;
        }
        return true;
    };

    const onSelectDate = (d) => {
        if (isExcludedDay(d)) {
            if (isSunday(d)) message.warning('Sundays cannot be selected.');
            else message.warning('Second Saturday cannot be selected.');
            return;
        }

        const iso = toISO(d);
        setPanelValue(d);

        setDates((prev) => {
            let next;

            if (prev.includes(iso)) next = prev.filter((x) => x !== iso);
            else next = [...prev, iso];

            if (next.length > 3) {
                message.error('You can select maximum 3 working days only.');
                return prev;
            }

            if (!isContinuousWorkingDates(next)) {
                message.error(
                    'Please select 1–3 continuous working days only (Sundays & 2nd Saturdays are skipped).'
                );
                return prev;
            }

            return next.sort();
        });
    };

    const fullCellRender = (current, info) => {
        if (info.type !== 'date') return info.originNode;

        const key = toISO(current);
        const selected = dates.includes(key);

        const sunday = isSunday(current);
        const secondSat = isSecondSaturday(current);
        const disabled = sunday || secondSat;

        const inViewMonth = dayjs(current).isSame(panelValue, 'month');
        const dimOpacity = inViewMonth ? 1 : 0.35;

        const disabledBg =
            'repeating-linear-gradient(45deg, rgba(239,68,68,0.08) 0, rgba(239,68,68,0.08) 6px, transparent 6px, transparent 12px)';

        const label = sunday
            ? 'Sunday (disabled)'
            : secondSat
                ? 'Second Saturday (disabled)'
                : !inViewMonth
                    ? 'Other month (light)'
                    : undefined;

        const cellStyle = {
            width: '100%',
            height: 34,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 6,
            userSelect: 'none',
            cursor: disabled ? 'not-allowed' : 'pointer',
            background: selected ? '#1677ff' : undefined,
            color: selected ? '#fff' : disabled ? '#ef4444' : inViewMonth ? undefined : '#9ca3af',
            fontWeight: selected ? 700 : inViewMonth ? 600 : 500,
            backgroundImage: !selected && disabled ? disabledBg : undefined,
            opacity: disabled ? 0.45 : dimOpacity,
        };

        return (
            <div
                style={cellStyle}
                title={label}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!disabled) onSelectDate(current);
                }}
            >
                {current.date()}
            </div>
        );
    };

    const inputDisplay = useMemo(() => {
        if (dates.length === 0) return '';
        const preview = dates.slice(0, 3).join(', ');
        const extra = dates.length - 3;
        return extra > 0 ? `${preview} +${extra}` : preview;
    }, [dates]);

    const isRichTextEmpty = (html) => {
        const text = (html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
        return text.length === 0;
    };

    const ACCEPTED_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];
    const MAX_MB = 5;

    const beforeUpload = (file) => {
        const okType =
            ACCEPTED_TYPES.includes(file.type) || (file.name || '').toLowerCase().endsWith('.pdf');

        if (!okType) {
            message.error('Only PDF, PNG, JPG, JPEG allowed.');
            return Upload.LIST_IGNORE;
        }

        const okSize = file.size / 1024 / 1024 <= MAX_MB;
        if (!okSize) {
            message.error(`File must be <= ${MAX_MB}MB.`);
            return Upload.LIST_IGNORE;
        }

        setAttachmentFile(file);
        setAttachmentList([
            { uid: file.uid || `${Date.now()}`, name: file.name, status: 'done' },
        ]);

        return false;
    };

    const removeAttachment = () => {
        setAttachmentFile(null);
        setAttachmentList([]);
    };

    const handleSendMail = () => {
        form
            .validateFields()
            .then((values) => {
                if (!leaveType) return message.error('Please select Leave Type.');
                if (!initialSubject?.trim()) return message.error('Subject should not be empty!');
                if (dates.length === 0) return message.error('Please add at least one date.');

                alertModal.confirm({
                    title: `Confirm Send Mail`,
                    content: 'Are you sure you want to send this mail?',
                    onOk() {
                        dispatch(
                            mailDataSubmit({
                                subject: initialSubject,
                                leaveType,
                                description: values.description,
                                dates,
                                attachment: attachmentFile,
                            })
                        );
                    },
                    onCancel() { },
                });
            })
            .catch((error) => console.error('Error while validating form:', error));
    };

    const leaveSummary = useMemo(() => {
        const list = Array.isArray(employeeLeavesRes)
            ? employeeLeavesRes
            : employeeLeavesRes?.payload || employeeLeavesRes?.data || [];
        const first = Array.isArray(list) && list.length ? list[0] : {};
        return {
            totalleavestaken: Number(first?.totalleavestaken ?? 0),
            clslLeaves: Number(first?.clslLeaves ?? 0),
            elLeaves: Number(first?.elLeaves ?? 0),
        };
    }, [employeeLeavesRes]);

    const leavesData = useMemo(() => {
        const raw = employeeLeavesRes;
        const list = Array.isArray(raw) ? raw : raw?.payload || raw?.data || [];
        return (Array.isArray(list) ? list : []).map((r, idx) => ({
            key: r?.id ?? `row-${idx}`,
            sno: idx + 1,
            reason: r?.reason ?? '-',
            leaveType: r?.leaveType ?? '-',
            leaveDates: Array.isArray(r?.leaveDates) ? r.leaveDates : [],
            leavesTaken:
                typeof r?.leavesTaken === 'number'
                    ? r.leavesTaken
                    : Array.isArray(r?.leaveDates)
                        ? r.leaveDates.length
                        : 0,
            createdOn: r?.createdOn ?? null,
            leavestatus: r?.status ?? null,
            totalLeaves: r?.totalLeaves ?? null,
        }));
    }, [employeeLeavesRes]);

    const statusColorMap = { SUBMITTED: 'blue', APPROVED: 'green', DECLINED: 'red' };

    const columns = [
        { title: 'S.No', dataIndex: 'sno', width: 80 },
        {
            title: 'Reason',
            dataIndex: 'reason',
            ellipsis: true,
            render: (text) => (
                <Tooltip title={text}>
                    <span
                        style={{
                            display: 'inline-block',
                            maxWidth: 260,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {text}
                    </span>
                </Tooltip>
            ),
        },
        {
            title: 'Leave Type',
            dataIndex: 'leaveType',
            width: 140,
            render: (t) => {
                const v = (t || '').toUpperCase();
                const color =
                    v === 'SICK'
                        ? 'green'
                        : v === 'CASUAL'
                            ? 'geekblue'
                            : v === 'EMERGENCY'
                                ? 'volcano'
                                : 'default';
                return (
                    <Tag color={color} style={{ fontWeight: 700 }}>
                        {t || '-'}
                    </Tag>
                );
            },
        },
        {
            title: 'Leave Dates',
            dataIndex: 'leaveDates',
            render: (arr = []) => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {(arr || []).map((d) => (
                        <Tag key={d}>{d}</Tag>
                    ))}
                </div>
            ),
        },
        { title: 'Total', dataIndex: 'leavesTaken', width: 90 },
        {
            title: 'Applied On',
            dataIndex: 'createdOn',
            width: 180,
            render: (val) => (val ? dayjs(val).format('YYYY-MM-DD HH:mm') : '-'),
        },
        {
            title: 'Leave Status',
            dataIndex: 'leavestatus',
            width: 120,
            render: (status) => {
                const s = (status || '').toUpperCase();
                return <Tag color={statusColorMap[s] || 'default'}>{status || '-'}</Tag>;
            },
        },
    ];

    const holidaysByISO = useMemo(() => {
        const list = Array.isArray(leaveData) ? leaveData : leaveData?.payload || leaveData?.data || [];
        const map = new Map();

        (Array.isArray(list) ? list : []).forEach((h) => {
            const iso = h?.date
                ? dayjs(h.date).add(5, 'hour').add(30, 'minute').format('YYYY-MM-DD')
                : null;

            if (!iso) return;

            const type = (h?.type || '').toUpperCase();
            if (!map.has(iso)) map.set(iso, []);
            map.get(iso).push({ name: h?.name || '-', type });
        });

        return map;
    }, [leaveData]);

    const leavesByISO = useMemo(() => {
        const map = new Map();

        (leavesData || []).forEach((row) => {
            const status = (row?.leavestatus || '').toUpperCase();
            if (status !== 'APPROVED') return;

            (row?.leaveDates || []).forEach((iso) => {
                if (!map.has(iso)) map.set(iso, []);
                map.get(iso).push({
                    status,
                    reason: row?.reason || '-',
                    leaveType: row?.leaveType || '-',
                });
            });
        });

        return map;
    }, [leavesData]);

    const clampText = (txt, max = 22) => {
        const s = String(txt || '');
        return s.length > max ? `${s.slice(0, max)}…` : s;
    };

    const modalCalendarCell = (current, info) => {
        if (info?.type && info.type !== 'date') return info.originNode;

        const iso = dayjs(current?.toDate ? current.toDate() : current).format('YYYY-MM-DD');
        const holidays = holidaysByISO.get(iso) || [];
        const leaves = leavesByISO.get(iso) || [];

        const publicHolidays = holidays.filter((h) => h.type === 'PUBLIC');
        const optionalHolidays = holidays.filter((h) => h.type === 'OPTIONAL');

        const hasLeave = leaves.length > 0;
        const hasPublic = publicHolidays.length > 0;
        const hasOptional = optionalHolidays.length > 0;

        const mode = hasLeave ? 'LEAVE' : hasPublic ? 'PUBLIC' : hasOptional ? 'OPTIONAL' : 'NONE';

        const bg =
            mode === 'LEAVE'
                ? 'rgba(124,58,237,0.16)'
                : mode === 'PUBLIC'
                    ? 'rgba(239,68,68,0.16)'
                    : mode === 'OPTIONAL'
                        ? 'rgba(22,163,74,0.16)'
                        : 'transparent';

        const border =
            mode === 'LEAVE'
                ? '1px solid rgba(124,58,237,0.40)'
                : mode === 'PUBLIC'
                    ? '1px solid rgba(239,68,68,0.40)'
                    : mode === 'OPTIONAL'
                        ? '1px solid rgba(22,163,74,0.40)'
                        : '1px solid transparent';

        const tooltipLines = [
            ...holidays.map((h) => `${h.type}: ${h.name}`),
            ...leaves.map((l) => `LEAVE (${l.status}) [${l.leaveType}]: ${l.reason}`),
        ];

        let line1 = '';
        let line2 = '';

        if (mode === 'LEAVE') {
            const first = leaves[0] || {};
            line1 = clampText(first.reason || 'Leave', 24);
            line2 = clampText((first.leaveType || '').toUpperCase(), 18);
            if (leaves.length > 1) line2 = `${line2} (+${leaves.length - 1})`;
        } else if (mode === 'PUBLIC') {
            const first = publicHolidays[0] || {};
            line1 = clampText(first.name || 'Holiday', 24);
            line2 = publicHolidays.length > 1 ? `(+${publicHolidays.length - 1})` : '';
        } else if (mode === 'OPTIONAL') {
            const first = optionalHolidays[0] || {};
            line1 = clampText(first.name || 'Holiday', 24);
            line2 = optionalHolidays.length > 1 ? `(+${optionalHolidays.length - 1})` : '';
        }

        const inViewMonth = dayjs(current?.toDate ? current.toDate() : current).isSame(modalCalValue, 'month');
        const dimOpacity = inViewMonth ? 1 : 0.35;

        const dayNumber = dayjs(current?.toDate ? current.toDate() : current).date();

        return (
            <Popover
                trigger="hover"
                placement="top"
                content={
                    <div style={{ fontSize: 12, maxWidth: 280, lineHeight: 1.4 }}>
                        {tooltipLines.length ? tooltipLines.map((t, i) => <div key={i}>• {t}</div>) : <div>No events</div>}
                    </div>
                }
            >
                <div
                    style={{
                        height: 62,
                        borderRadius: 14,
                        padding: 8,
                        background: bg,
                        border,
                        opacity: dimOpacity,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            minHeight: 16,
                            paddingLeft: 2,
                            fontWeight: 800,
                            color: inViewMonth ? '#111827' : '#9ca3af',
                        }}
                    >
                        {dayNumber}
                    </div>

                    {mode !== 'NONE' ? (
                        <div
                            style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                textAlign: 'center',
                                lineHeight: 1.15,
                                padding: '0 8px',
                                gap: 2,
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 12,
                                    fontWeight: 900,
                                    color: '#111827',
                                    maxWidth: '100%',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                                title={line1}
                            >
                                {line1}
                            </div>

                            {!!line2 && (
                                <div
                                    style={{
                                        fontSize: 11,
                                        color: '#374151',
                                        fontWeight: 800,
                                        maxWidth: '100%',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                    title={line2}
                                >
                                    {line2}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div style={{ flex: 1 }} />
                    )}
                </div>
            </Popover>
        );
    };

    const modalHeaderRender = ({ value, onChange }) => {
        const current = dayjs(value?.toDate ? value.toDate() : value);
        const year = current.year();
        const month = current.month();

        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const yearOptions = [];
        for (let y = year - 5; y <= year + 5; y += 1) yearOptions.push(y);

        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '6px 8px 12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Button size="small" onClick={() => onChange(current.subtract(1, 'month'))}>◀</Button>
                    <Button size="small" onClick={() => onChange(current.add(1, 'month'))}>▶</Button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Select
                        size="small"
                        value={month}
                        style={{ width: 110 }}
                        onChange={(m) => onChange(current.month(m))}
                        options={monthNames.map((label, idx) => ({ label, value: idx }))}
                    />
                    <Select
                        size="small"
                        value={year}
                        style={{ width: 90 }}
                        onChange={(y) => onChange(current.year(y))}
                        options={yearOptions.map((y) => ({ label: y, value: y }))}
                    />
                </div>

                <div style={{ width: 64 }} />
            </div>
        );
    };

    const openLeavesModal = () => {
        setLeavesModalTab('Calendar');
        setLeavesModalOpen(true);
        dispatch(getEmployeeLeavesData());
        dispatch(getLeavesDataRequestData());
    };

    const ToggleBtn = ({ active, children, onClick }) => (
        <button
            type="button"
            onClick={onClick}
            style={{
                border: '1px solid rgba(124,58,237,0.25)',
                background: active ? '#7c3aed' : '#ffffff',
                color: active ? '#fff' : '#111827',
                padding: '7px 12px',
                borderRadius: 999,
                fontWeight: 800,
                cursor: 'pointer',
                boxShadow: active ? '0 6px 16px rgba(124,58,237,0.18)' : 'none',
                transition: 'all .15s ease',
                minWidth: 96,
                textAlign: 'center',
            }}
        >
            {children}
        </button>
    );

    return (
        <>
            <Main>
                {isPersonResLoading || isMailAddResLoading ? (
                    <div className="custom-loading-spin-with-text">
                        <CustomSpinner />
                        <p style={{ fontWeight: 'bold', color: '#073886' }}>Loading please wait...</p>
                    </div>
                ) : (
                    <div className="d-flex justify-content-center" ref={containerRef}>
                        <Cards headless>
                            <div className="body">
                                {/* Top row */}
                                <div
                                    className="group"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'flex-end',
                                        justifyContent: 'space-between',
                                        gap: 12,
                                        flexWrap: 'wrap',
                                        marginTop: 6,
                                        marginBottom: 10,
                                    }}
                                >
                                    <div style={{ minWidth: 260, maxWidth: 320, flex: '1 1 260px' }}>
                                        <strong>Leave Type:</strong>
                                        <div style={{ marginTop: 6 }}>
                                            <Select
                                                value={leaveType}
                                                onChange={(v) => setLeaveType(v)}
                                                placeholder="Select leave type"
                                                style={{ width: '100%' }}
                                                options={[
                                                    { label: 'Casual', value: 'CASUAL' },
                                                    { label: 'Sick', value: 'SICK' },
                                                    { label: 'Emergency', value: 'EMERGENCY' },
                                                ]}
                                            />
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'flex-end', flex: '1 1 auto' }}>
                                        <Button
                                            size="small"
                                            type="primary"
                                            onClick={openLeavesModal}
                                            style={{ background: '#7c3aed', borderColor: '#7c3aed' }}
                                        >
                                            View Leave History
                                        </Button>
                                    </div>
                                </div>

                                {/* Dates */}
                                <div className="group mt-10">
                                    <strong>Dates:</strong>

                                    <div style={{ marginTop: 4, color: '#918989ff' }}>
                                        Select <b>1 day</b> or up to <b>3 continuous working days</b> (Sundays & <b>2nd Saturdays</b> not allowed).
                                    </div>

                                    <Space direction="vertical" size="small" style={{ width: '100%', marginTop: 8 }}>
                                        <Space>
                                            <Popover
                                                open={calendarOpen}
                                                onOpenChange={setCalendarOpen}
                                                trigger="click"
                                                placement="bottomLeft"
                                                destroyTooltipOnHide
                                                overlayInnerStyle={{ padding: 8 }}
                                                overlayStyle={{ width: 300 }}
                                                getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
                                                content={
                                                    <div style={{ width: '100%' }}>
                                                        <Calendar
                                                            fullscreen={false}
                                                            value={panelValue}
                                                            onPanelChange={(v) => setPanelValue(v)}
                                                            onSelect={onSelectDate}
                                                            fullCellRender={fullCellRender}
                                                        />

                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                                                            <div style={{ display: 'flex', gap: 12, alignItems: 'center', color: '#9b2f2fff', fontSize: 12 }}>
                                                                <span>
                                                                    <span style={{ color: '#ef4444', fontWeight: 600 }}>Sunday & 2nd Saturday</span> (disabled)
                                                                </span>
                                                                <span>• Selected = blue</span>
                                                                <span style={{ color: '#6b7280' }}>• Other month dates are light</span>
                                                            </div>
                                                            <Button size="small" onClick={() => setCalendarOpen(false)}>
                                                                Done
                                                            </Button>
                                                        </div>
                                                    </div>
                                                }
                                            >
                                                <Input
                                                    readOnly
                                                    onClick={() => setCalendarOpen(true)}
                                                    placeholder="Select 1–3 continuous working dates"
                                                    value={inputDisplay}
                                                    style={{ width: 260, cursor: 'pointer' }}
                                                />
                                            </Popover>

                                            <Tooltip title="Remove all selected dates">
                                                <Button size="small" onClick={() => setDates([])} disabled={dates.length === 0}>
                                                    Clear all
                                                </Button>
                                            </Tooltip>
                                        </Space>

                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                            {dates.map((d) => (
                                                <Tag
                                                    key={d}
                                                    closable
                                                    onClose={(e) => {
                                                        e.preventDefault();
                                                        setDates((prev) => prev.filter((x) => x !== d));
                                                    }}
                                                >
                                                    {d}
                                                </Tag>
                                            ))}
                                            {dates.length === 0 && <span style={{ color: '#999' }}>No dates selected.</span>}
                                        </div>

                                        <div
                                            style={{
                                                background: '#fff8e1',
                                                border: '1px solid #ffe089',
                                                color: '#7a5d00',
                                                padding: '8px 10px',
                                                borderRadius: 8,
                                                fontSize: 12,
                                            }}
                                        >
                                            Please include the selected dates in your email description.
                                        </div>
                                    </Space>
                                </div>

                                {/* Attachment */}
                                <div className="mt-10 group">
                                    <strong>Attachment (PDF / Image):</strong>
                                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
                                        <Upload
                                            accept=".pdf,.png,.jpg,.jpeg"
                                            multiple={false}
                                            maxCount={1}
                                            beforeUpload={beforeUpload}
                                            fileList={attachmentList}
                                            onRemove={() => {
                                                removeAttachment();
                                                return true;
                                            }}
                                            showUploadList={{ showRemoveIcon: true }}
                                        >
                                            <Button type="secondary" size="small">
                                                <UploadOutlined style={{ marginRight: 6 }} />
                                                Choose File
                                            </Button>
                                        </Upload>

                                        {attachmentFile ? (
                                            <span style={{ color: '#374151', fontSize: 12 }}>
                                                Selected: <b>{attachmentFile.name}</b>
                                            </span>
                                        ) : (
                                            <span style={{ color: '#6b7280', fontSize: 12 }}>(Optional) Max {MAX_MB}MB</span>
                                        )}
                                    </div>
                                </div>

                                {/* Subject */}
                                <div className="mt-10 group">
                                    <strong>Subject:</strong>
                                    <Input
                                        onChange={(e) => setInitialSubject(e?.target?.value ?? '')}
                                        placeholder="Enter subject"
                                        type="text"
                                        value={initialSubject}
                                    />
                                </div>

                                {/* Body */}
                                <div className="mt-10">
                                    <div style={{ border: '2px solid #073886' }}>
                                        <Form form={form} layout="vertical">
                                            <Form.Item
                                                name="description"
                                                label=""
                                                rules={[
                                                    {
                                                        validator: (_, value) => {
                                                            if (isRichTextEmpty(value)) return Promise.reject(new Error('Please enter description'));
                                                            return Promise.resolve();
                                                        },
                                                    },
                                                ]}
                                            >
                                                <MailComposer text initialValue={''} />
                                            </Form.Item>
                                        </Form>
                                    </div>

                                    <div className="text-right mt-10">
                                        <Button onClick={handleSendMail} type="primary">
                                            Send
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Cards>

                        {/* Modal */}
                        <Modal
                            styles={{ header: { paddingRight: 110 } }}
                            title={
                                <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                    <span style={{ fontWeight: 800 }}>Leave Overview</span>

                                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginRight: 36 }}>
                                        <ToggleBtn active={leavesModalTab === 'Calendar'} onClick={() => setLeavesModalTab('Calendar')}>
                                            Calendar
                                        </ToggleBtn>
                                        <ToggleBtn active={leavesModalTab === 'History'} onClick={() => setLeavesModalTab('History')}>
                                            History
                                        </ToggleBtn>
                                    </div>
                                </div>
                            }
                            open={leavesModalOpen}
                            onCancel={() => setLeavesModalOpen(false)}
                            footer={null}
                            width={leavesModalTab === 'Calendar' ? 980 : 960}
                        >
                            {leavesModalTab === 'Calendar' ? (
                                <div>
                                    {/* SUMMARY */}
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 10,
                                            alignItems: 'center',
                                            marginBottom: 10,
                                            padding: 10,
                                            borderRadius: 12,
                                            background: 'rgba(0,0,0,0.02)',
                                            border: '1px solid rgba(0,0,0,0.06)',
                                        }}
                                    >
                                        <Tag color="blue" style={{ margin: 0, fontWeight: 700 }}>
                                            totalleavestaken: {leaveSummary.totalleavestaken}
                                        </Tag>
                                        <Tag color="geekblue" style={{ margin: 0, fontWeight: 700 }}>
                                            casual/sick: {leaveSummary.clslLeaves}
                                        </Tag>
                                        <Tag color="volcano" style={{ margin: 0, fontWeight: 700 }}>
                                            Emergency: {leaveSummary.elLeaves}
                                        </Tag>
                                    </div>

                                    {/* Legend */}
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 10,
                                            alignItems: 'center',
                                            marginBottom: 12,
                                            padding: 10,
                                            borderRadius: 12,
                                            background: 'rgba(0,0,0,0.02)',
                                            border: '1px solid rgba(0,0,0,0.06)',
                                        }}
                                    >
                                        <Tag color="red" style={{ margin: 0, fontWeight: 600 }}>
                                            PUBLIC Holiday
                                        </Tag>
                                        <Tag color="green" style={{ margin: 0, fontWeight: 600 }}>
                                            OPTIONAL Holiday
                                        </Tag>
                                        <Tag color="purple" style={{ margin: 0, fontWeight: 600 }}>
                                            Leave (APPROVED)
                                        </Tag>
                                        {/* <span style={{ color: '#6b7280', fontSize: 12 }}>
                                            Legend shows types. Cells show only reason/name. Prev/next month is light.
                                        </span> */}
                                    </div>

                                    <div style={{ padding: 12, borderRadius: 14, border: '1px solid rgba(0,0,0,0.08)', background: '#fff' }}>
                                        <Calendar
                                            fullscreen={false}
                                            value={modalCalValue}
                                            onChange={(v) => setModalCalValue(v)}
                                            onPanelChange={(v) => setModalCalValue(v)}
                                            headerRender={modalHeaderRender}
                                            dateFullCellRender={(current) => modalCalendarCell(current, { type: 'date', originNode: null })}
                                            cellRender={(current, info) => modalCalendarCell(current, info)}
                                            fullCellRender={(current, info) => modalCalendarCell(current, info)}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <Table
                                    dataSource={leavesData}
                                    columns={columns}
                                    size="middle"
                                    pagination={{ pageSize: 5 }}
                                    scroll={{ x: true }}
                                />
                            )}
                        </Modal>
                    </div>
                )}
            </Main>
        </>
    );
}

export default LeaveEmail;
